import React from "react";
import BaseModal from "react-modal";

import cx from "classnames";

import { useIsMounted } from "../../helpers/useIsMounted";
import { Button } from "../Button";
import { Dialog as _Dialog } from "../Dialog";
import Header from "../Header";
import { Icon } from "../Icon";
import { ModalVerticalSpace } from "../ModalVerticalSpace";

import styles from "./Modal.scss";

export interface ModalProps {
  /**
   * When the user requests from within the modal that we want to close it, we call this function. This must be set for closing via clicking away, the "close" button, and pressing escape.
   */
  onClose?: () => void;
  /**
   * Default max width of the modal. Default is "medium" (480px)
   */
  size?: "small" | "medium_deprecated" | "medium" | "large" | "full";
  /**
   * Whether or not the modal is open. Defaults to false
   */
  isOpen?: boolean;
  /**
   * The element (usually an image or a large banner square) to display as the full-width modal header
   */
  fullWidthHeader?: React.ReactNode | null;
  /**
   * Title of the modal.
   */
  modalTitle?: React.ReactNode | string;
  /**
   * Passed to the base modal for customizing the close button
   */
  closeButtonUnderlayClass?: string;
  /**
   * Class name of the modal container
   */
  className?: string;
  /**
   * Whether or not the modal should close when the overlay is clicked. Defaults to true
   */
  shouldCloseOnOverlayClick?: boolean;
  /**
   * Collapses the spacing between title and content when set to true. False by
   */
  hideTitleSpacing?: boolean;
}

export const SIZES = {
  small: styles["modal-small"],
  medium_deprecated: styles["modal-medium-deprecated"],
  medium: styles["modal-medium"],
  large: styles["modal-large"],
  full: styles["modal-full"],
};

/**
 * @deprecated All updates should be made to the {@link _Dialog} component and
 * existing implementations updated to not use props requiring BaseModal.
 */
export function Modal({
  children,
  isOpen,
  onClose,
  fullWidthHeader = null,
  className,
  closeButtonUnderlayClass = "",
  modalTitle,
  size = "medium",
  shouldCloseOnOverlayClick = true,
  hideTitleSpacing = false,
}: React.PropsWithChildren<ModalProps>) {
  const classNames = cx(styles["modal"], SIZES[size]);
  const containerClass = cx(styles["content-container"], className);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [overlayStyles, setOverlayStyles] = React.useState({});
  const isMounted = useIsMounted();

  // This resizes our top and bottom padding on the overlay if the modal content
  // changes.
  // istanbul ignore next - runtime UI stuff that's very difficult to simulate in tests without sacrificing the quality of the test
  React.useEffect(() => {
    setOverlayStyles(getOverlayStyles(contentRef));
    // Watch this on an interval, because modals change content and size. We want
    // the modal to stretch to the full height of the screen when the content
    // changes.
    const interval = setInterval(() => {
      if (!isMounted()) {
        return;
      }
      setOverlayStyles(getOverlayStyles(contentRef));
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const handleClose = () => {
    onClose?.();
  };

  const onAfterOpen = () => {
    setOverlayStyles(getOverlayStyles(contentRef));
  };

  return (
    <BaseModal
      className={classNames}
      isOpen={Boolean(isOpen)}
      onRequestClose={handleClose}
      onAfterOpen={onAfterOpen}
      overlayClassName={styles["overlay"]}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      ariaHideApp={false}
      style={{
        overlay: overlayStyles,
      }}
      contentRef={(node) => (contentRef.current = node)}
    >
      <ModalNavigation onClose={handleClose} />
      {fullWidthHeader && (
        <div className={styles["full-width-header"]}>{fullWidthHeader}</div>
      )}
      <div className={cx(styles["content-wrapper"], containerClass)}>
        <div
          data-testid="close-button-underlay"
          className={cx(
            styles["close-button-underlay"],
            closeButtonUnderlayClass
          )}
        />
        <ModalTitle
          modalTitle={modalTitle}
          hideTitleSpacing={hideTitleSpacing}
        />
        {children}
      </div>
    </BaseModal>
  );
}

/**
 * Takes a ref to the modal element and returns a style object that we apply to
 * the overlay. Ideally, we just return the paddingTop and paddingBottom. We do
 * this so that we can position modals in the middle of the screen without
 * resorting to absolute positioning, that can overflow the frame.
 */
// istanbul ignore next - runtime UI stuff that's very difficult to simulate in tests without sacrificing the quality of the test
function getOverlayStyles(modalElementRef: React.RefObject<HTMLDivElement>) {
  if (!modalElementRef.current) {
    return {};
  }

  const element = modalElementRef.current;
  const modalHeight = element.offsetHeight;
  const viewportHeight = document.documentElement.clientHeight;
  const difference = viewportHeight - modalHeight;

  if (difference < 20) {
    return {};
  }

  const padding = Math.floor(difference / 2);
  const paddingStr = `${padding}px`;
  return {
    paddingTop: paddingStr,
    paddingBottom: paddingStr,
  };
}

function ModalNavigation({ onClose }: { onClose: () => void }) {
  return (
    <div className={styles.navigation}>
      <Button
        variant="transparent"
        onClick={onClose}
        className={styles["close-button"]}
        data-testid="close-modal-button"
      >
        <Icon icon="close" />
      </Button>
    </div>
  );
}

function ModalTitle({
  modalTitle,
  hideTitleSpacing,
}: {
  modalTitle?: React.ReactNode;
  hideTitleSpacing: boolean;
}) {
  return modalTitle ? (
    <>
      <Header size="h3" level="2">
        {modalTitle}
      </Header>
      {!hideTitleSpacing && <ModalVerticalSpace />}
    </>
  ) : null;
}
